/**
 * usePrint – lightweight print / PDF-export hook.
 *
 * Usage:
 *   const { printRef, handlePrint } = usePrint();
 *   <div ref={printRef}> … </div>
 *   <button onClick={handlePrint}>Print</button>
 *
 * The hook injects a temporary <style> that hides everything except the
 * target element while the browser's print dialog is open, then cleans up
 * automatically when printing completes.
 */

import { useRef, useCallback } from 'react';

export function usePrint(title?: string) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useCallback(() => {
    const el = printRef.current;
    if (!el) return;

    // Save original page title, optionally set a custom one
    const originalTitle = document.title;
    if (title) document.title = title;

    // Clone the content so we can inject it into a hidden iframe for printing
    const styleSheets = Array.from(document.styleSheets)
      .map(sheet => {
        try {
          return Array.from(sheet.cssRules)
            .map(r => r.cssText)
            .join('\n');
        } catch {
          // cross-origin stylesheets – reference by href instead
          return sheet.href
            ? `@import url("${sheet.href}");`
            : '';
        }
      })
      .join('\n');

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document;
    if (!iframeDoc) return;

    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html lang="${document.documentElement.lang}" dir="${document.documentElement.dir || 'ltr'}">
        <head>
          <meta charset="UTF-8" />
          <title>${document.title}</title>
          <style>${styleSheets}</style>
          <style>
            /* Print-specific overrides */
            body { background: #fff !important; font-family: inherit; }
            .no-print { display: none !important; }
            @page { size: A4; margin: 15mm 12mm; }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          </style>
        </head>
        <body>${el.innerHTML}</body>
      </html>
    `);
    iframeDoc.close();

    iframe.contentWindow?.focus();

    // Give styles time to apply, then print
    setTimeout(() => {
      iframe.contentWindow?.print();
      // Cleanup after print dialog closes
      setTimeout(() => {
        document.body.removeChild(iframe);
        document.title = originalTitle;
      }, 1000);
    }, 400);
  }, [title]);

  return { printRef, handlePrint };
}

export default usePrint;
